import React, { useState, useRef, useEffect, useContext } from "react";
import styles from "./ActionReplays.module.scss";
import TinySlider from "tiny-slider-react";
import SliderControls from "./SliderButton";
import sliderStyles from "./TinySlider.module.scss";
import { animOnSwipe } from "../utils/animation";
import { isMobile, isIOS, isTablet } from "react-device-detect";
import ActionReplayPlaceholder from "../components/ActionReplayPlaceholder";
import { AzureMP } from "react-azure-mp";
import { Context } from "../Store";
import ReactPlayer from "react-player/lazy";
import Dots from "./Dots";
import useSliderDots from "../hooks/useSliderDots";
import { eventNames } from "../utils/eventData";
import { capitalize } from "../utils/helpers";
import ShareArrow from "./ShareArrow";

// Inner component — owns sliderRef, useSliderDots, animOnSwipe entirely.
// Keyed by selectedPlayerId in the parent so it fully remounts on player change
// with no stale refs or mid-flight effect calls.
const ActionReplaySlider = ({ videos, isTimeline, players, handleHover, openLightbox, stateData }) => {
  const sliderRef = useRef();
  const sliderId = useRef(`slider-${Math.random().toString(36).substr(2, 9)}`).current;
  const [isFirst, setIsFirst] = useState(true);
  const [isLast, setIsLast] = useState(false);

  const maxDots = 2;
  const { currentIndex, visibleDots, goToSlide } = useSliderDots(sliderRef, videos.length, maxDots);

  const settings = {
    lazyload: true,
    touch: true,
    nav: false,
    controls: false,
    mouseDrag: true,
    loop: false,
    items: 1,
    gutter: isTimeline ? 10 : 5,
    responsive: {
      640: {
        items: Math.max(1, Math.min(videos.length, 2)),
        edgePadding: isTimeline ? 0 : 30,
        gutter: isTimeline ? 10 : 5,
      },
      1024: {
        items: Math.max(1, Math.min(videos.length, 4)),
        edgePadding: isTimeline ? 0 : 30,
        gutter: isTimeline ? 10 : 5,
      },
    },
  };

  useEffect(() => {
    const slider = sliderRef.current?.slider;
    if (!slider) return;

    animOnSwipe(slider);

    const updatePosition = (info) => {
      setIsFirst(info.index === 0);
      setIsLast(info.index + info.items >= info.slideCount);
    };

    // set initial state
    updatePosition(slider.getInfo());
    slider.events.on("indexChanged", updatePosition);
    return () => slider.events.off("indexChanged", updatePosition);
  }, []);

  const showDots = ((isMobile || isTablet) && videos.length > 1) || (!isMobile && !isTablet && videos.length > 4);

  return (
    <div id={sliderId} className={styles.sliderContainer}>
      {videos.length >= 3 && <SliderControls slider={sliderRef} isFirst={isFirst} isLast={isLast} />}

      <TinySlider settings={settings} ref={sliderRef}>
        {videos.map((video, index) => (
          <div key={index} style={{ position: "relative" }} className={styles.actionReplayItem}>
            <div
              onClick={() => openLightbox(index)}
              onMouseEnter={isMobile ? () => false : (e) => handleHover(index, e)}
              onMouseLeave={(e) => handleHover(index, e)}
              style={{ cursor: "pointer" }}
            >
              {!video.thumbnail_url && <ActionReplayPlaceholder video={{ video }} players={{ players }} />}
              {video.thumbnail_url && <img src={video.thumbnail_url} alt="" />}

              <div className={styles.actionReplayText}>
                <span className={styles.actioReplayTitle}>
                  {capitalize(players.find((p) => p.id === Number(video.players))?.name)}: {eventNames[video.type]}
                </span>
              </div>
            </div>

            <ShareArrow
              title={`${capitalize(players.find((p) => p.id === Number(video.players))?.name)}: ${eventNames[video.type]}`}
              url={video.video_url}
              text={`Check out this ${eventNames[video.type]} Action Replay from our Flight Club visit.`}
              downloadUrl={video.video_url}
              isVideo={true}
              customBottom="-10px"
              sectionName="Action Replay"
              mediaShare={{
                mediaType: "VIDEO",
                mediaURI: video.asset_uri,
                videoURL: video.video_url,
                sectionName: "Action Replay",
                thumbnail: video.thumbnail_url,
                videoType: video.type,
              }}
              cityurl={stateData.cityurl}
              venue={stateData.venue}
              azureurl={stateData.azureurl}
              videourl={stateData.videourl}
              guid={stateData.guid}
              country={stateData.country}
            />
          </div>
        ))}
      </TinySlider>

      {showDots && <Dots visibleDots={visibleDots} currentIndex={currentIndex} goToSlide={goToSlide} />}
    </div>
  );
};

const ActionReplays = (props) => {
  const [state] = useContext(Context);
  const videos = props.videos || state.actionReplays;
  const [selectedPlayerId, setSelectedPlayerId] = useState("all");
  const players = state.players || [];

  const playersWithReplays = players
    .filter((player) => videos.some((video) => Number(video.players) === Number(player.id)))
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));

  const filteredVideos =
    selectedPlayerId === "all" ? videos : videos.filter((video) => Number(video.players) === Number(selectedPlayerId));

  const isTimeline = props.section === "timeline";

  const actionReplaysMedia = filteredVideos.map((video) => ({
    mediaType: "VIDEO",
    mediaURI: video.asset_uri,
    mediaProvider: video.video_provider,
    videoURL: video.video_url,
    sectionName: "Action Replay",
    thumbnail: video.thumbnail_url,
    videoType: video.type,
    eventName: eventNames[video.type],
  }));

  const actionReplaysVideos = filteredVideos.map((video) => {
    if (video.video_provider === "Azure") {
      return (
        <div className={styles.actionReplayItem}>
          <AzureMP
            options={{ controls: true, autoplay: true, muted: true }}
            src={[{ src: video.video_url, type: "application/vnd.ms-sstr+xml" }]}
          />
        </div>
      );
    } else if (video.video_provider === "AzureBlob") {
      return (
        <div className={styles.actionReplayItem}>
          <ReactPlayer
            className="react-player"
            url={video.video_url}
            muted
            controls
            playing
            width="100%"
            height="100%"
          />
        </div>
      );
    } else {
      return (
        <iframe
          id={"video" + video.asset_uri}
          src={`https://player.vimeo.com/video/${video.asset_uri}?title=0&portait=0&byline=0&loop=true&api=1&playsinline=true&player_id=video${video.asset_uri}`}
          frameBorder="0"
          width="1920px"
          height="1080px"
        />
      );
    }
  });

  if (state.debug) console.log(actionReplaysMedia);

  const handleHover = (index, e) => {
    props.handleHover(
      {
        section: "ActionReplay",
        index,
        ...actionReplaysMedia[index],
        target: e.currentTarget,
        timeStamp: e.timeStamp,
        groupLength: actionReplaysMedia.length,
        sources: actionReplaysVideos,
        mediashare: actionReplaysMedia,
      },
      e.type,
    );
  };

  const openLightbox = (index) => {
    props.openLightbox({
      section: "ActionReplay",
      index,
      sources: actionReplaysVideos,
      mediashare: actionReplaysMedia,
    });
  };

  const showDots =
    ((isMobile || isTablet) && filteredVideos.length > 1) || (!isMobile && !isTablet && filteredVideos.length > 4);

  return (
    <div
      id={props.section !== "timeline" ? "action-replays" : undefined}
      className={`${props.section !== "timeline" ? "section" : ""} scale-slider section--with-share-arrow ${showDots ? "section--padded-extra" : "section--padded"}`}
    >
      {props.section !== "timeline" && (
        <>
          <h2>Game Highlights</h2> <p className="subheading">The throws worth watching twice.</p>
        </>
      )}
      <div className={styles.filterRow}>
        <label htmlFor="action-replay-player-filter" className={styles.filterLabel}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M22 3H2L10 12.46V19L14 21V12.46L22 3Z"
              stroke="#1E1E1E"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </label>
        <select
          id="action-replay-player-filter"
          className={styles.filterSelect}
          value={selectedPlayerId}
          onChange={(e) => setSelectedPlayerId(e.target.value)}
        >
          <option value="all">All players</option>
          {playersWithReplays.map((player) => (
            <option key={player.id} value={player.id}>
              {capitalize(player.name)}
            </option>
          ))}
        </select>
      </div>

      <ActionReplaySlider
        key={selectedPlayerId}
        videos={filteredVideos}
        isTimeline={isTimeline}
        players={players}
        handleHover={handleHover}
        openLightbox={openLightbox}
        stateData={state}
      />
    </div>
  );
};

export default ActionReplays;
